// Who sees a file of mine, changed in one dialog over its page (byj-1wv.51.17,
// RFC-054 §4): people added by address, with what came of each; who sees it
// now — its viewers and the classes given it, rows as a template's people
// and classes are (TemplatePage, byj-1wv.51.14), each removed or taken back
// behind its ⋯, Give to a class on the classes' heading (GiveToClass) — put
// second so what is so is never below what could be; and an invite link for
// a viewer. A dialog rather than a drawer: it is a task finished and closed,
// the page under it unchanged until it is, and on a phone it takes the
// screen. Its head is FormDialog's; its Esc and Tab every modal dialog's
// (components/Modal), a popover opened in it (Give to a class) taking Esc
// first; closed, it gives the focus back to Share. Its open links, and the
// links made in it, are the page's (`open`, useOpenInvites): the relay keeps
// only a link's hash, so one made here is shown nowhere else.
import { useEffect, useRef, useState, type FormEvent, type ReactNode, type Ref } from "react";
import type { FilePersonResult, ShareFileDetail } from "../api";
import { t, phrases } from "../i18n";
import { GiveToClass, useTeaching } from "./GiveToClass";
import { InviteLinkForm, type InviteChoice } from "./InviteLink";
import { useModalKeys } from "./Modal";
import { InviteLinksChip, type OpenInvites } from "./OpenInvites";
import { RowMenu } from "./RowMenu";
import { EmptyRows, initialOf } from "./Rows";
import type { FileApi } from "../files";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** The id Share names in aria-controls, so the dialog gives the focus back to it as it closes. */
export const fileShareId = "file-share";

/** A section of the dialog: its heading — with what acts on it at the line's end — a line saying what it does, then what it holds. */
function Section({ id, title, lead, count, action, headRef, children }: {
  id: string;
  title: string;
  lead?: string;
  count?: number;
  action?: ReactNode;
  /** given, the heading takes the focus from code alone: where it goes once what acts on the section has gone */
  headRef?: Ref<HTMLHeadingElement>;
  children: ReactNode;
}) {
  return (
    <section className="file-share-section" aria-labelledby={id}>
      <div className="row file-share-title-line">
        <h4 id={id} ref={headRef} tabIndex={headRef ? -1 : undefined} className="grow file-share-title">
          {title} {count !== undefined && <span className="tab-count">{count}</span>}
        </h4>
        {action}
      </div>
      {lead && <p className="muted small file-share-lead">{lead}</p>}
      {children}
    </section>
  );
}

export function FileShare({ id, detail, client, reload, open, onClose }: {
  id: string;
  detail: ShareFileDetail;
  client: Pick<FileApi, "createFileInvite" | "addFilePeople" | "removeFileViewer" | "giveFile" | "takeFile" | "classes">;
  reload: () => Promise<void>;
  /** its open links and those made on the page, the page's (useOpenInvites), shown on the link's heading (byj-1wv.76) */
  open: OpenInvites;
  onClose: () => void;
}) {
  const [choice, setChoice] = useState<InviteChoice>({ role: "viewer", uses: 1, ttl: phrases.ttlOptions()[2]![0] });
  const teaching = useTeaching(client.classes);
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState("");
  const [added, setAdded] = useState<FilePersonResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLTextAreaElement>(null);
  const classesHead = useRef<HTMLHeadingElement>(null);
  const keys = useModalKeys(onClose);
  useEffect(() => {
    // as FormDialog does: the focus in its first field, and back on Share as it closes
    const opener = document.querySelector<HTMLElement>(`[aria-controls="${fileShareId}"]`);
    first.current?.focus();
    return () => opener?.focus();
  }, []);
  const given = detail.classes ?? [];
  const viewers = detail.viewers ?? [];
  const givable = (teaching ?? []).filter((c) => !given.some((g) => g.id === c.id));
  /** A change, then the file as the relay has it after; true once both are done, false with the dialog saying why. */
  const act = async (f: () => Promise<unknown>): Promise<boolean> => {
    setError(null);
    try {
      await f();
      await reload();
      return true;
    } catch (err) {
      setError(errText(err));
      return false;
    }
  };
  const add = (e: FormEvent) => {
    e.preventDefault();
    const list = emails.split(/[\s,;]+/).filter(Boolean);
    void act(async () => {
      const r = await client.addFilePeople(id, list, note.trim());
      setAdded(r.results);
      setEmails("");
      setNote("");
    });
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div id={fileShareId} className="modal file-share-modal" role="dialog" aria-modal="true" aria-labelledby={`${fileShareId}-title`} onKeyDown={keys} data-testid="file-people">
        <div className="row file-share-head">
          <h3 id={`${fileShareId}-title`} className="grow flush file-name-strong">
            {t("files:fileShareTitle", { name: detail.file.name })}
          </h3>
          <button type="button" className="small" aria-label={t("common:close")} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="file-share-body">
          {error && (
            <p className="error" role="alert">
              {t("common:error", { m: error })}
            </p>
          )}

          <Section id="file-share-add" title={t("files:fileAddPeople")} lead={t("files:fileAddLead")}>
            <form onSubmit={add} data-testid="file-add-people">
              <label className="field">
                {t("files:fileEmails")}
                <textarea ref={first} value={emails} onChange={(e) => setEmails(e.target.value)} rows={2} required />
              </label>
              <label className="field">
                {t("files:fileNote")}
                <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
              </label>
              <div className="row">
                <span className="grow" />
                <button type="submit" className="small primary">
                  {t("files:fileAdd")}
                </button>
              </div>
              {added.length > 0 && (
                <ul className="file-share-added small" data-testid="file-added">
                  {added.map((r) => (
                    <li key={r.email} className={r.error ? "error" : undefined}>
                      {phrases.fileAdded(r)}
                    </li>
                  ))}
                </ul>
              )}
            </form>
          </Section>

          <Section id="file-share-viewers" title={t("files:fileViewers")} count={viewers.length}>
            {viewers.length === 0 ? (
              <EmptyRows text={t("files:fileNoViewers")} />
            ) : (
              <ul className="rows" data-testid="file-viewers">
                {viewers.map((u) => (
                  <li key={u.id} className="share-row member-row is-live">
                    <span className="row-lead" />
                    <span className="kind-box" aria-hidden="true">
                      {initialOf(u.name || u.email)}
                    </span>
                    <div className="row-main">
                      <span className="row-title">{u.name || u.email}</span>
                      {u.name && <div className="row-sub">{u.email}</div>}
                    </div>
                    <div className="row-meta" />
                    <RowMenu
                      label={t("shares:moreFor", { title: u.name || u.email })}
                      items={[{ label: t("files:fileRemoveViewer"), onSelect: () => void act(() => client.removeFileViewer(id, u.id)), danger: true }]}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section
            id="file-share-classes"
            title={t("templates:templateClasses")}
            count={given.length}
            headRef={classesHead}
            action={
              // a class they teach that does not have it yet, picked beside the list it joins; none left, no button
              <GiveToClass givable={givable} note={t("files:fileGiveLead")} give={(cls) => act(() => client.giveFile(id, cls))} focusAfterLast={() => classesHead.current?.focus()} />
            }
          >
            {given.length === 0 ? (
              // nobody but an instructor gives a class anything: one known to teach none is told why there is no button
              <EmptyRows text={teaching?.length === 0 ? `${t("templates:templateNoClasses")} ${t("templates:templateNoClassTaught")}` : t("templates:templateNoClasses")} />
            ) : (
              <ul className="rows" data-testid="file-classes">
                {given.map((c) => (
                  <li key={c.id} className="share-row class-list-row is-live">
                    <span className="row-lead" />
                    <span className="kind-box" aria-hidden="true">
                      {initialOf(c.name)}
                    </span>
                    <div className="row-main">
                      <span className="row-title">{c.name}</span>
                      <div className="row-sub">
                        <code>{t("classes:classCode", { code: c.code })}</code>
                      </div>
                    </div>
                    <div className="row-meta" />
                    <RowMenu label={t("shares:moreFor", { title: c.name })} items={[{ label: t("templates:templateTake"), onSelect: () => void act(() => client.takeFile(id, c.id)) }]} />
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section
            id="file-share-link"
            title={t("session:inviteCreate")}
            lead={t("files:fileInviteLead")}
            action={<InviteLinksChip open={open} own />}
          >
            <InviteLinkForm
              id={`${id}-invite`}
              choice={choice}
              onChoice={setChoice}
              create={(c) => client.createFileInvite(id, c.uses, c.ttl)}
              made={open.made}
              onMade={open.add}
            />
          </Section>
        </div>
        <div className="row file-share-foot">
          <p className="muted small flush grow">{t("files:fileShareSignedIn")}</p>
          <button type="button" className="primary small" onClick={onClose}>
            {t("files:fileDone")}
          </button>
        </div>
      </div>
    </div>
  );
}
